import { ArrowUp, Bell, ListTodo } from 'lucide-react'
import { Badge, Card, EmptyState } from '@/components/play'
import {
  feedbackKindBadgeTone,
  feedbackKindLabels,
  feedbackPriorityBadgeTone,
  feedbackPriorityLabels,
  feedbackStatusBadgeTone,
  feedbackStatusLabels,
  formatRelativeTime,
} from '@/lib/feedback'
import type { FeedbackItemResource, FeedbackStatus } from '@/types/feedback'

interface RoadmapBoardProps {
  items: FeedbackItemResource[]
}

const columns: FeedbackStatus[] = ['planned', 'in_progress']

/** Planned and In progress side by side; each item is a Play Card that opens it. */
export function RoadmapBoard({ items }: RoadmapBoardProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {columns.map((status) => {
        const columnItems = items.filter((item) => item.attributes.status === status)
        const headingId = `roadmap-${status}`

        return (
          <section key={status} aria-labelledby={headingId} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 id={headingId} className="text-xl font-semibold text-neutral-10">{feedbackStatusLabels[status]}</h2>
              <Badge tone={feedbackStatusBadgeTone(status)}>
                {columnItems.length}
                <span className="sr-only"> items</span>
              </Badge>
            </div>
            {columnItems.map((item) => (
              <Card
                key={item.id}
                as="article"
                headingLevel={3}
                title={item.attributes.title}
                action={{ to: `/feedback/${item.id}`, label: item.attributes.title }}
              >
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={feedbackKindBadgeTone(item.attributes.kind)}>{feedbackKindLabels[item.attributes.kind]}</Badge>
                    {item.attributes.priority ? (
                      <Badge tone={feedbackPriorityBadgeTone(item.attributes.priority)}>{feedbackPriorityLabels[item.attributes.priority]}</Badge>
                    ) : null}
                  </div>
                  <p className="text-sm text-neutral-7">{item.attributes.description_markdown}</p>
                  <p className="flex flex-wrap items-center gap-3 text-xs text-neutral-7">
                    <span className="inline-flex items-center gap-1"><ArrowUp className="h-4 w-4" aria-hidden="true" />{item.attributes.votes_count}<span className="sr-only"> votes</span></span>
                    <span className="inline-flex items-center gap-1"><Bell className="h-4 w-4" aria-hidden="true" />{item.attributes.followers_count}<span className="sr-only"> followers</span></span>
                    <span>Updated {formatRelativeTime(item.attributes.updated_at)}</span>
                  </p>
                </div>
              </Card>
            ))}
            {columnItems.length === 0 ? (
              <EmptyState size="compact" headingLevel={3} icon={<ListTodo />} title="Nothing here yet" />
            ) : null}
          </section>
        )
      })}
    </div>
  )
}
